// ═══════════════════════════════════════════════════════════════
// KOTCH - COMBAT UTILITIES
// Combat resolution logic for lane duels and rounds
// ═══════════════════════════════════════════════════════════════

import type {
  CardInstance,
  CardDefinition,
  Lane,
  LaneCommitment,
  LaneDuelResult,
  LaneDuelOutcome,
  RoundResult,
  PlayerId,
  Player,
  LaneRewardResult,
} from '../../types/kotch';
import { LANE_REWARDS } from '../../types/kotch';
import { LANES } from '../../types/kotch';
import { CARD_DEFINITIONS, COMBAT_TRIANGLE } from '../../data/kotch/cards';
import { FACTION_ABILITIES } from '../../data/kotch/factionDecks';
import { getTotalPower } from './deckUtils';

// ─────────────────────────────────────────────────────────────────
// CARD LOOKUP HELPER
// ─────────────────────────────────────────────────────────────────

function getCardDef(instance: CardInstance): CardDefinition {
  const def = CARD_DEFINITIONS[instance.definitionId];
  if (!def) {
    throw new Error(`Card definition not found: ${instance.definitionId}`);
  }
  return def;
}

// ─────────────────────────────────────────────────────────────────
// SINGLE LANE DUEL RESOLUTION
// ─────────────────────────────────────────────────────────────────

export function resolveLaneDuel(
  player1Card: CardInstance | null,
  player2Card: CardInstance | null,
  lane: Lane
): LaneDuelResult {
  // Both lanes empty
  if (!player1Card && !player2Card) {
    return {
      lane,
      player1Card: null,
      player2Card: null,
      outcome: 'empty',
      winningPlayerId: null,
      reason: 'Both lanes empty',
    };
  }

  // Unopposed cases
  if (!player1Card && player2Card) {
    return {
      lane,
      player1Card: null,
      player2Card,
      outcome: 'unopposed_p2',
      winningPlayerId: 2,
      reason: 'Unopposed',
    };
  }

  if (player1Card && !player2Card) {
    return {
      lane,
      player1Card,
      player2Card: null,
      outcome: 'unopposed_p1',
      winningPlayerId: 1,
      reason: 'Unopposed',
    };
  }

  // Both cards present - resolve combat
  const def1 = getCardDef(player1Card!);
  const def2 = getCardDef(player2Card!);

  const result = resolveCardVsCard(def1, def2);

  return {
    lane,
    player1Card,
    player2Card,
    outcome: result.outcome,
    winningPlayerId: result.winnerId,
    reason: result.reason,
  };
}

interface CardVsCardResult {
  outcome: LaneDuelOutcome;
  winnerId: PlayerId | null;
  reason: string;
}

function resolveCardVsCard(card1: CardDefinition, card2: CardDefinition): CardVsCardResult {
  const type1 = card1.type;
  const type2 = card2.type;

  // Same type: compare power
  if (type1 === type2) {
    return resolveSameType(card1, card2);
  }

  // Handle special card interactions
  if (card1.category === 'special' || card2.category === 'special') {
    return resolveSpecialCombat(card1, card2);
  }

  // Basic vs Basic: use combat triangle
  return resolveBasicCombat(card1, card2);
}

function resolveSameType(card1: CardDefinition, card2: CardDefinition): CardVsCardResult {
  if (card1.power > card2.power) {
    return {
      outcome: 'player1_wins',
      winnerId: 1,
      reason: `Higher power (${card1.power} vs ${card2.power})`,
    };
  }
  if (card2.power > card1.power) {
    return {
      outcome: 'player2_wins',
      winnerId: 2,
      reason: `Higher power (${card2.power} vs ${card1.power})`,
    };
  }
  return {
    outcome: 'draw',
    winnerId: null,
    reason: `Same power (${card1.power})`,
  };
}

function resolveBasicCombat(card1: CardDefinition, card2: CardDefinition): CardVsCardResult {
  // Check combat triangle: card1.type beats what?
  const card1Beats = COMBAT_TRIANGLE[card1.type];

  if (card1Beats === card2.type) {
    return {
      outcome: 'player1_wins',
      winnerId: 1,
      reason: `${capitalize(card1.type)} beats ${capitalize(card2.type)}`,
    };
  }

  // If card1 doesn't beat card2, then card2 beats card1
  return {
    outcome: 'player2_wins',
    winnerId: 2,
    reason: `${capitalize(card2.type)} beats ${capitalize(card1.type)}`,
  };
}

function resolveSpecialCombat(card1: CardDefinition, card2: CardDefinition): CardVsCardResult {
  const type1 = card1.type;
  const type2 = card2.type;

  // Knight vs Basic: Knight wins
  if (type1 === 'knight' && card2.category === 'basic') {
    return {
      outcome: 'player1_wins',
      winnerId: 1,
      reason: 'Knight defeats basic unit',
    };
  }
  if (type2 === 'knight' && card1.category === 'basic') {
    return {
      outcome: 'player2_wins',
      winnerId: 2,
      reason: 'Knight defeats basic unit',
    };
  }

  // Spy vs Knight: Spy wins
  if (type1 === 'spy' && type2 === 'knight') {
    return {
      outcome: 'player1_wins',
      winnerId: 1,
      reason: 'Spy assassinates Knight',
    };
  }
  if (type2 === 'spy' && type1 === 'knight') {
    return {
      outcome: 'player2_wins',
      winnerId: 2,
      reason: 'Spy assassinates Knight',
    };
  }

  // Spy vs Basic: Basic wins
  if (type1 === 'spy' && card2.category === 'basic') {
    return {
      outcome: 'player2_wins',
      winnerId: 2,
      reason: 'Basic unit exposes Spy',
    };
  }
  if (type2 === 'spy' && card1.category === 'basic') {
    return {
      outcome: 'player1_wins',
      winnerId: 1,
      reason: 'Basic unit exposes Spy',
    };
  }

  // Knight vs Knight OR Spy vs Spy: compare power
  return resolveSameType(card1, card2);
}

// ─────────────────────────────────────────────────────────────────
// ROUND RESOLUTION
// ─────────────────────────────────────────────────────────────────

export function resolveRound(
  player1Commitment: LaneCommitment,
  player2Commitment: LaneCommitment,
  roundNumber: number,
  attackerId: PlayerId
): RoundResult {
  // Resolve each lane
  const laneResults: LaneDuelResult[] = LANES.map(lane =>
    resolveLaneDuel(player1Commitment[lane], player2Commitment[lane], lane)
  );

  // Count lane wins
  const player1LanesWon = laneResults.filter(r => r.winningPlayerId === 1).length;
  const player2LanesWon = laneResults.filter(r => r.winningPlayerId === 2).length;
  const draws = laneResults.filter(r => r.outcome === 'draw' || r.outcome === 'empty').length;

  // Calculate total power for tiebreakers
  const p1Cards = [
    player1Commitment.left,
    player1Commitment.center,
    player1Commitment.right,
  ].filter((c): c is CardInstance => c !== null);

  const p2Cards = [
    player2Commitment.left,
    player2Commitment.center,
    player2Commitment.right,
  ].filter((c): c is CardInstance => c !== null);

  const player1TotalPower = getTotalPower(p1Cards);
  const player2TotalPower = getTotalPower(p2Cards);

  // Determine round winner
  const { winnerId, winReason } = determineRoundWinner(
    player1LanesWon,
    player2LanesWon,
    player1TotalPower,
    player2TotalPower
  );

  return {
    roundNumber,
    attackerId,
    laneResults,
    player1LanesWon,
    player2LanesWon,
    draws,
    player1TotalPower,
    player2TotalPower,
    winnerId,
    winReason,
  };
}

function determineRoundWinner(
  p1Lanes: number,
  p2Lanes: number,
  p1Power: number,
  p2Power: number
): { winnerId: PlayerId | null; winReason: string } {
  // Win 2 or more lanes = win round
  if (p1Lanes >= 2) {
    return { winnerId: 1, winReason: `Won ${p1Lanes} lane${p1Lanes > 1 ? 's' : ''}` };
  }
  if (p2Lanes >= 2) {
    return { winnerId: 2, winReason: `Won ${p2Lanes} lane${p2Lanes > 1 ? 's' : ''}` };
  }

  // Tied lanes (1-1 or 0-0 with draws): compare total power
  if (p1Lanes === p2Lanes) {
    if (p1Power > p2Power) {
      return { winnerId: 1, winReason: `Higher total power (${p1Power} vs ${p2Power})` };
    }
    if (p2Power > p1Power) {
      return { winnerId: 2, winReason: `Higher total power (${p2Power} vs ${p1Power})` };
    }
    return { winnerId: null, winReason: 'Draw - equal lanes and power' };
  }

  // One player has 1 lane, other has 0 (but didn't reach 2)
  // This shouldn't happen in normal play, but handle it
  if (p1Lanes > p2Lanes) {
    return { winnerId: 1, winReason: `Won ${p1Lanes} lane vs ${p2Lanes}` };
  }
  return { winnerId: 2, winReason: `Won ${p2Lanes} lane vs ${p1Lanes}` };
}

// ─────────────────────────────────────────────────────────────────
// BATTLE END DETECTION
// ─────────────────────────────────────────────────────────────────

export function isBattleOver(
  player1Hand: CardInstance[],
  player2Hand: CardInstance[]
): boolean {
  // Battle ends when BOTH players have exhausted all cards
  return player1Hand.length === 0 && player2Hand.length === 0;
}

export interface RoutingResult {
  isRouted: boolean;
  routedPlayerId: PlayerId | null;  // The player who ran out of cards
  winnerPlayerId: PlayerId | null;
  bonusPoints: number;              // Remaining cards count
}

/**
 * Check if one player has been routed (ran out of cards while opponent still has cards).
 * The player with remaining cards wins and gains points equal to their remaining card count.
 */
export function checkForRouting(
  player1Hand: CardInstance[],
  player2Hand: CardInstance[]
): RoutingResult {
  const p1Empty = player1Hand.length === 0;
  const p2Empty = player2Hand.length === 0;

  // If both have cards or both are empty, no routing
  if (p1Empty === p2Empty) {
    return { isRouted: false, routedPlayerId: null, winnerPlayerId: null, bonusPoints: 0 };
  }

  // One player is routed
  if (p1Empty && !p2Empty) {
    return {
      isRouted: true,
      routedPlayerId: 1,
      winnerPlayerId: 2,
      bonusPoints: player2Hand.length,
    };
  }

  // p2Empty && !p1Empty
  return {
    isRouted: true,
    routedPlayerId: 2,
    winnerPlayerId: 1,
    bonusPoints: player1Hand.length,
  };
}

export function determineBattleWinner(
  player1RoundWins: number,
  player2RoundWins: number,
  roundsPlayed: RoundResult[]
): { winnerId: PlayerId | null; winReason: string } {
  if (player1RoundWins > player2RoundWins) {
    return {
      winnerId: 1,
      winReason: `Won ${player1RoundWins} rounds to ${player2RoundWins}`,
    };
  }
  if (player2RoundWins > player1RoundWins) {
    return {
      winnerId: 2,
      winReason: `Won ${player2RoundWins} rounds to ${player1RoundWins}`,
    };
  }

  // Tied on rounds - check last round
  if (roundsPlayed.length > 0) {
    const lastRound = roundsPlayed[roundsPlayed.length - 1];
    if (lastRound.winnerId) {
      return {
        winnerId: lastRound.winnerId,
        winReason: 'Won final round (tiebreaker)',
      };
    }

    // Last round was also a draw - compare total power across battle
    const totalP1Power = roundsPlayed.reduce((sum, r) => sum + r.player1TotalPower, 0);
    const totalP2Power = roundsPlayed.reduce((sum, r) => sum + r.player2TotalPower, 0);

    if (totalP1Power > totalP2Power) {
      return {
        winnerId: 1,
        winReason: `Higher total battle power (${totalP1Power} vs ${totalP2Power})`,
      };
    }
    if (totalP2Power > totalP1Power) {
      return {
        winnerId: 2,
        winReason: `Higher total battle power (${totalP2Power} vs ${totalP1Power})`,
      };
    }
  }

  // Ultimate tie - shouldn't happen often
  return {
    winnerId: null,
    winReason: 'Draw - no tiebreaker available',
  };
}

// ─────────────────────────────────────────────────────────────────
// MATCH END DETECTION
// ─────────────────────────────────────────────────────────────────

export function isMatchOver(
  player1BattleWins: number,
  player2BattleWins: number,
  battlesToWin: number = 2
): boolean {
  return player1BattleWins >= battlesToWin || player2BattleWins >= battlesToWin;
}

export function determineMatchWinner(
  player1BattleWins: number,
  player2BattleWins: number,
  battlesToWin: number = 2
): PlayerId | null {
  if (player1BattleWins >= battlesToWin) return 1;
  if (player2BattleWins >= battlesToWin) return 2;
  return null;
}

// ─────────────────────────────────────────────────────────────────
// LANE REWARDS
// ─────────────────────────────────────────────────────────────────

export interface PendingAbility {
  type: 'discard_opponent_hand';
  winnerId: PlayerId;
  loserId: PlayerId;
  eligibleCardIds: string[];
}

export interface ApplyLaneRewardsResult {
  updatedPlayers: Record<PlayerId, Player>;
  rewards: LaneRewardResult[];
  persistentCard: { card: CardInstance; playerId: PlayerId } | null;  // Motra ability
  pendingAbility: PendingAbility | null;  // Hydaria ability (requires player choice)
}

/**
 * Applies lane rewards based on lane duel results.
 * - Left lane winner: Reveals a random card in opponent's hand
 * - Center lane winner: Triggers faction-specific ability
 * - Right lane winner: Discards top card of opponent's deck
 *
 * commitments are needed for Migora's return_flank_card ability.
 */
export function applyLaneRewards(
  laneResults: LaneDuelResult[],
  players: Record<PlayerId, Player>,
  p1Commitment: LaneCommitment,
  p2Commitment: LaneCommitment
): ApplyLaneRewardsResult {
  const rewards: LaneRewardResult[] = [];
  let persistentCard: { card: CardInstance; playerId: PlayerId } | null = null;
  let pendingAbility: PendingAbility | null = null;

  // Deep copy players to avoid mutations
  const updatedPlayers: Record<PlayerId, Player> = {
    1: { ...players[1], hand: [...players[1].hand], matchDeck: [...players[1].matchDeck], discardPile: [...players[1].discardPile], revealedCards: [...players[1].revealedCards] },
    2: { ...players[2], hand: [...players[2].hand], matchDeck: [...players[2].matchDeck], discardPile: [...players[2].discardPile], revealedCards: [...players[2].revealedCards] },
  };

  laneResults.forEach(result => {
    // Skip if no winner (draw or empty)
    if (!result.winningPlayerId) return;

    const winnerId = result.winningPlayerId;
    const loserId: PlayerId = winnerId === 1 ? 2 : 1;
    const reward = LANE_REWARDS[result.lane];

    const rewardResult: LaneRewardResult = {
      lane: result.lane,
      winnerId,
      rewardType: reward.type,
      applied: false,
    };

    switch (reward.type) {
      case 'reveal_card': {
        // Left lane: Reveal a random unrevealed card in opponent's hand
        const loserHand = updatedPlayers[loserId].hand;
        const revealedIds = updatedPlayers[loserId].revealedCards;
        const unrevealedCards = loserHand.filter(c => !revealedIds.includes(c.instanceId));

        if (unrevealedCards.length > 0) {
          const randomIndex = Math.floor(Math.random() * unrevealedCards.length);
          const cardToReveal = unrevealedCards[randomIndex];
          const cardDef = CARD_DEFINITIONS[cardToReveal.definitionId];

          updatedPlayers[loserId].revealedCards.push(cardToReveal.instanceId);
          rewardResult.applied = true;
          rewardResult.details = `Revealed ${cardDef?.name || cardDef?.type} ${cardDef?.power}`;
        } else {
          rewardResult.details = 'No unrevealed cards';
        }
        break;
      }

      case 'faction_ability': {
        // Center lane: trigger winner's faction ability
        const winnerFaction = updatedPlayers[winnerId].faction;
        const ability = winnerFaction ? FACTION_ABILITIES[winnerFaction] : null;
        const effect = ability?.effect || 'draw_card';

        switch (effect) {
          case 'draw_card': {
            // Rigata / Vera: Draw a card from deck
            const winnerDeck = updatedPlayers[winnerId].matchDeck;
            if (winnerDeck.length > 0) {
              const drawnCard = winnerDeck.shift()!;
              updatedPlayers[winnerId].hand.push(drawnCard);
              const cardDef = CARD_DEFINITIONS[drawnCard.definitionId];
              rewardResult.applied = true;
              rewardResult.details = `Drew ${cardDef?.name || cardDef?.type} ${cardDef?.power}`;
            } else {
              rewardResult.details = 'Deck empty';
            }
            break;
          }

          case 'return_flank_card': {
            // Migora: Return one of your flank cards from this round to hand
            // The card was already moved to discard, so pull it back
            const winnerCommitment = winnerId === 1 ? p1Commitment : p2Commitment;
            const flankCards: CardInstance[] = [];
            if (winnerCommitment.left) flankCards.push(winnerCommitment.left);
            if (winnerCommitment.right) flankCards.push(winnerCommitment.right);

            if (flankCards.length > 0) {
              // Pick one: prefer the card from a lost lane, else just pick the first
              const lostFlankCard = flankCards.find(fc => {
                const laneResult = laneResults.find(lr =>
                  (lr.lane === 'left' || lr.lane === 'right') &&
                  ((winnerId === 1 && lr.player1Card?.instanceId === fc.instanceId) ||
                   (winnerId === 2 && lr.player2Card?.instanceId === fc.instanceId))
                );
                return laneResult && laneResult.winningPlayerId !== winnerId;
              });
              const cardToReturn = lostFlankCard || flankCards[0];

              // Remove from discard pile and add back to hand
              const discardIdx = updatedPlayers[winnerId].discardPile.findIndex(
                c => c.instanceId === cardToReturn.instanceId
              );
              if (discardIdx !== -1) {
                updatedPlayers[winnerId].discardPile.splice(discardIdx, 1);
                updatedPlayers[winnerId].hand.push(cardToReturn);
                const cardDef = CARD_DEFINITIONS[cardToReturn.definitionId];
                rewardResult.applied = true;
                rewardResult.details = `Returned ${cardDef?.name || cardDef?.type} ${cardDef?.power} to hand`;
              }
            } else {
              rewardResult.details = 'No flank cards played';
            }
            break;
          }

          case 'discard_opponent_hand': {
            // Hydaria: Player chooses a card to discard from opponent's hand
            // Basic cards always eligible; special cards only if revealed
            const loserHand = updatedPlayers[loserId].hand;
            const revealedIds = updatedPlayers[loserId].revealedCards;

            const eligibleCards = loserHand.filter(c => {
              const def = CARD_DEFINITIONS[c.definitionId];
              if (!def) return false;
              if (def.category === 'basic') return true;
              // Special cards only if revealed
              return revealedIds.includes(c.instanceId);
            });

            if (eligibleCards.length > 0) {
              // Don't auto-discard — flag as pending for player choice
              pendingAbility = {
                type: 'discard_opponent_hand',
                winnerId,
                loserId,
                eligibleCardIds: eligibleCards.map(c => c.instanceId),
              };
              rewardResult.applied = true;
              rewardResult.details = 'Choose a card to discard from opponent\'s hand';
            } else {
              rewardResult.details = 'No eligible cards in opponent\'s hand';
            }
            break;
          }

          case 'persist_center_card': {
            // Motra: The center lane winning card persists to next round on a flank
            const winnerCommitment = winnerId === 1 ? p1Commitment : p2Commitment;
            const centerCard = winnerCommitment.center;

            if (centerCard) {
              // Remove from discard pile (it was already moved there)
              const discardIdx = updatedPlayers[winnerId].discardPile.findIndex(
                c => c.instanceId === centerCard.instanceId
              );
              if (discardIdx !== -1) {
                updatedPlayers[winnerId].discardPile.splice(discardIdx, 1);
              }
              // Store as persistent card (will be placed on a flank next round)
              persistentCard = { card: centerCard, playerId: winnerId };
              const cardDef = CARD_DEFINITIONS[centerCard.definitionId];
              rewardResult.applied = true;
              rewardResult.details = `${cardDef?.name || cardDef?.type} ${cardDef?.power} persists to next round`;
            } else {
              rewardResult.details = 'No center card to persist';
            }
            break;
          }
        }
        break;
      }

      case 'discard_deck': {
        // Right lane: Discard top card of opponent's deck
        const loserDeck = updatedPlayers[loserId].matchDeck;

        if (loserDeck.length > 0) {
          const discardedCard = loserDeck.shift()!;
          updatedPlayers[loserId].discardPile.push(discardedCard);
          const cardDef = CARD_DEFINITIONS[discardedCard.definitionId];
          rewardResult.applied = true;
          rewardResult.details = `Discarded ${cardDef?.name || cardDef?.type} ${cardDef?.power}`;
        } else {
          rewardResult.details = 'Deck empty';
        }
        break;
      }
    }

    rewards.push(rewardResult);
  });

  return { updatedPlayers, rewards, persistentCard, pendingAbility };
}

// ─────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getCommitmentAsList(commitment: LaneCommitment): CardInstance[] {
  return [commitment.left, commitment.center, commitment.right]
    .filter((c): c is CardInstance => c !== null);
}

export function countCommittedCards(commitment: LaneCommitment): number {
  let count = 0;
  if (commitment.left) count++;
  if (commitment.center) count++;
  if (commitment.right) count++;
  return count;
}

export function getCommittedLanes(commitment: LaneCommitment): Lane[] {
  const lanes: Lane[] = [];
  if (commitment.left) lanes.push('left');
  if (commitment.center) lanes.push('center');
  if (commitment.right) lanes.push('right');
  return lanes;
}
